import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/utils';

const pillVariants = cva(
  'inline-block items-left gap-2 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bhanzu-orange disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-white/10 text-white',
        solid: 'bg-magical-moonlight-d1 text-white',
        outline: 'border border-white/30 bg-transparent text-white',
        ghost: 'bg-transparent text-white hover:bg-white/10',
        orange: 'bg-bhanzu-orange text-white',
        blue: 'bg-blue-chaos text-white',
        success: 'bg-success-green text-white',
        warning: 'bg-warning-yellow text-magical-moonlight',
        error: 'bg-alert-red text-white',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-3 py-1 text-sm',
        lg: 'px-4 py-2 text-base',
      },
      rounded: {
        true: 'rounded-full',
        false: 'rounded-lg',
      },
      maxWidth: {
        none: '',
        xs: 'max-w-xs',
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        '2xl': 'max-w-2xl',
        '20': 'max-w-20',
        '24': 'max-w-24',
        '32': 'max-w-32',
        '40': 'max-w-40',
        '48': 'max-w-48',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      rounded: false,
      maxWidth: 'none',
    },
  }
);

export interface PillProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?:
    | 'default'
    | 'solid'
    | 'outline'
    | 'ghost'
    | 'orange'
    | 'blue'
    | 'success'
    | 'warning'
    | 'error';
  size?: 'sm' | 'md' | 'lg';
  rounded?: boolean;
  maxWidth?:
    | 'none'
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '20'
    | '24'
    | '32'
    | '40'
    | '48';
  children: React.ReactNode;
}

const Pill = React.forwardRef<HTMLDivElement, PillProps>(
  (
    { className, variant, size, rounded, maxWidth, children, ...props },
    ref
  ) => {
    const shouldTruncate = maxWidth && maxWidth !== 'none';

    return (
      <div
        className={cn(
          pillVariants({ variant, size, rounded, maxWidth, className }),
          shouldTruncate && 'truncate'
        )}
        ref={ref}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Pill.displayName = 'Pill';

export { Pill, pillVariants };
